/** @jsx jsx */
import { type SerializedStyles, css, jsx } from '@emotion/react';

import React, { useState } from 'react';

import { ButtonBase, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';
import { useBlockLayoutTheme } from 'bundles/ent-website/hooks/useBlockLayoutTheme';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';

import _t from 'i18n!nls/ent-website';

export type SelectedValue = 'left' | 'right';

export type TabToggleProps = {
  /**
   * Native id attribute
   */
  id?: string;
  /**
   * Label for the left option
   */
  leftLabel: string;
  /**
   * Label for the right option
   */
  rightLabel: string;
  /**
   * Initial selected value
   */
  initialSelected: SelectedValue;
  /**
   * Callback when selection changes
   */
  onChange: (selected: SelectedValue) => void;
  /**
   * Optional aria-label for the toggle group
   */
  ariaLabel?: string;
  /**
   * Custom CSS styles using emotion
   */
  css?: SerializedStyles;
  /**
   * Module for tracking
   */
  module: BlockLevelModule;
  /**
   * Position of the toggle tab
   */
  position: number;
};

const styles = {
  container: (isInverted: boolean) => css`
    display: flex;
    padding: 3px;
    justify-content: space-between;
    align-items: center;
    border-radius: var(--cds-border-radius-max);
    width: fit-content;
    gap: var(--cds-spacing-50);
    color: inherit;
    margin: var(--cds-spacing-300) 0;

    /* Light theme styles */
    background-color: var(--cds-color-grey-25);
    border: 1px solid var(--cds-color-grey-100);

    /* Dark theme overrides */
    ${isInverted &&
    css`
      background-color: rgb(255 255 255 / 20%);
      border: 1px solid var(--cds-color-grey-400);
    `}
  `,

  tab: (isInverted: boolean) => css`
    padding: var(--cds-spacing-150) var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-max);
    font-weight: 600;
    min-width: 100px;
    color: inherit;

    &:focus::after {
      border-radius: 100px;
    }

    &:hover {
      .label {
        text-decoration-line: underline;
        text-decoration-style: solid;
        text-decoration-skip-ink: auto;
        text-decoration-thickness: auto;
        text-underline-offset: auto;
        text-underline-position: from-font;
      }
    }

    &.active {
      /* Light theme active styles */
      color: var(--cds-color-neutral-background-primary);
      background-color: var(--cds-color-interactive-secondary);

      /* Dark theme active overrides */
      ${isInverted &&
      css`
        color: var(--cds-color-neutral-primary);
        background-color: var(--cds-color-white-0);
      `}
    }
  `,

  label: (isInverted: boolean) => css`
    color: inherit;
    white-space: nowrap;

    &.active {
      /* Light theme active text */
      color: var(--cds-color-neutral-background-primary);

      /* Dark theme active text override */
      ${isInverted &&
      css`
        color: var(--cds-color-neutral-primary);
      `}
    }

    &:not(.active) {
      /* Light theme inactive text */
      color: var(--cds-color-neutral-primary-weak);

      /* Dark theme inactive text override */
      ${isInverted &&
      css`
        color: var(--cds-color-neutral-primary-invert);
      `}
    }
  `,
};

/**
 * ToggleTab is a binary toggle component that allows users to switch between two options.
 *
 */
const ToggleTab = ({
  leftLabel,
  rightLabel,
  initialSelected,
  onChange,
  ariaLabel,
  id,
  css: customCss,
  module,
  position,
}: TabToggleProps) => {
  const [selected, setSelected] = useState<SelectedValue>(initialSelected);
  const { isInverted } = useBlockLayoutTheme();
  const track = useTracker();

  const handleChange = (selectedValue: SelectedValue) => {
    const trackingName = eventingTransform(
      `${module.__typename}_${module.slug}_button_${position}_${selectedValue === 'left' ? leftLabel : rightLabel}`
    );
    track('click_button', {
      button: {
        name: 'ent_website_contentful_button',
        linkName: trackingName,
      },
    });
    setSelected(selectedValue);
    onChange(selectedValue);
  };

  return (
    <div
      css={[styles.container(isInverted), customCss]}
      role="tablist"
      aria-label={ariaLabel || _t('Tab Toggle')}
      id={id}
      data-testid="tab-toggle-container"
    >
      <ButtonBase
        css={styles.tab(isInverted)}
        className={selected === 'left' ? 'active' : ''}
        onClick={() => handleChange('left')}
        role="tab"
        aria-selected={selected === 'left'}
        data-testid="toggle-tab-left"
      >
        <Typography2
          css={styles.label(isInverted)}
          className={`label ${selected === 'left' ? 'active' : ''}`}
          variant="actionSecondary"
          component="span"
        >
          {leftLabel}
        </Typography2>
      </ButtonBase>
      <ButtonBase
        css={styles.tab(isInverted)}
        className={selected === 'right' ? 'active' : ''}
        onClick={() => handleChange('right')}
        role="tab"
        aria-selected={selected === 'right'}
        data-testid="toggle-tab-right"
      >
        <Typography2
          css={styles.label(isInverted)}
          className={`label ${selected === 'right' ? 'active' : ''}`}
          variant="actionSecondary"
          component="span"
        >
          {rightLabel}
        </Typography2>
      </ButtonBase>
    </div>
  );
};

export default ToggleTab;
